import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UserService, UserDto, SearchUsersParams } from '../../../services/api/user';
import { PageResponse } from '../../../services/api/page-response';
import { AuthService, GUEST_EMAIL_SUFFIX } from '../../../services/auth/auth';
import { LanguageService } from '../../../services/i18n/language';
import { ConfirmDialogService } from '../../../services/confirm-dialog';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

type SortKey = 'fullName' | 'email' | 'phone' | 'createdAt';

interface RoleTab {
  key: string;            // 'all' или код роли
  labelKey: string;
  fallback: string;
}

/** Вкладки ролей в порядке показа; вкладки без пользователей скрываются. */
const ROLE_TABS: RoleTab[] = [
  { key: 'all', labelKey: 'admin.users.tabs.all', fallback: 'Все' },
  { key: 'ROLE_CARGO_OWNER', labelKey: 'admin.users.tabs.cargoOwner', fallback: 'Грузовладельцы' },
  { key: 'ROLE_FREIGHT_FORWARDER', labelKey: 'admin.users.tabs.forwarder', fallback: 'Экспедиторы' },
  { key: 'ROLE_SUPPLIER', labelKey: 'admin.users.tabs.supplier', fallback: 'Поставщики' },
  { key: 'ROLE_CUSTOMS_BROKER', labelKey: 'admin.users.tabs.broker', fallback: 'Таможенные брокеры' },
  { key: 'ROLE_PUBLIC', labelKey: 'admin.users.tabs.public', fallback: 'Без роли' },
  { key: 'ROLE_ADMIN', labelKey: 'admin.users.tabs.admin', fallback: 'Администраторы' },
  { key: 'guests', labelKey: 'admin.users.tabs.guests', fallback: 'Гости' }
];

/** Гость — запись, которую сайт заводит клиенту до ввода телефона: логин «uuid@guest.bilload.kz». */
function isGuestUser(user: UserDto): boolean {
  return !!user.email?.toLowerCase().endsWith(GUEST_EMAIL_SUFFIX);
}

/**
 * Кто попадает во вкладку: «Все» — все, кроме гостей; «Гости» — гостевые записи;
 * вкладки ролей — пользователи этой роли без гостей (гости тоже ROLE_PUBLIC).
 */
function inTab(user: UserDto, key: string): boolean {
  if (key === 'guests') return isGuestUser(user);
  if (isGuestUser(user)) return false;
  if (key === 'all') return true;
  return user.role?.code === key;
}

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './users-list.html',
  styleUrl: './users-list.scss'
})
export class UsersListComponent implements OnInit, OnDestroy {
  users: UserDto[] = [];
  filteredUsers: UserDto[] = [];
  loading = false;
  error: string | null = null;
  
  searchForm: FormGroup;
  searchTerm = '';
  
  // Pagination
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;
  
  // Translations
  titleText = 'Управление пользователями';
  searchPlaceholderText = 'Поиск по имени, email или телефону...';
  createUserText = 'Создать пользователя';
  nameText = 'Имя';
  emailText = 'Email';
  phoneText = 'Телефон';
  roleText = 'Роль';
  actionsText = 'Действия';
  viewText = 'Просмотр';
  editText = 'Редактировать';
  deleteText = 'Удалить';
  noUsersText = 'Пользователи не найдены';
  loadingText = 'Загрузка...';
  registeredText = 'Регистрация';

  // Сортировка по любому столбцу; по умолчанию — сначала новые
  sortKey: SortKey = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';
  readonly columns: { key: SortKey; label: () => string }[] = [
    { key: 'fullName', label: () => this.nameText },
    { key: 'email', label: () => this.emailText },
    { key: 'phone', label: () => this.phoneText },
    { key: 'createdAt', label: () => this.registeredText }
  ];

  /** Заголовки столбцов: «Имя», «Email · Телефон», «Регистрация». */
  readonly columnGroups = [
    this.columns.filter(c => c.key === 'fullName'),
    this.columns.filter(c => c.key === 'email' || c.key === 'phone'),
    this.columns.filter(c => c.key === 'createdAt')
  ];
  
  activeRole = 'all';
  roleMenuOpen = false;

  private subscriptions = new Subscription();

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private confirmDialog: ConfirmDialogService
  ) {
    this.searchForm = this.fb.group({
      searchTerm: ['']
    });
  }

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('UsersList: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('UsersList: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_ADMIN') {
        console.warn('UsersList: User is not admin, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      this.loadUsers();
      this.updateTranslations();
      
      // Подписка на изменения поискового запроса с debounce
      this.subscriptions.add(
        this.searchForm.get('searchTerm')?.valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged()
        ).subscribe(value => {
          this.searchTerm = value;
          this.currentPage = 0;
          this.searchUsers();
        }) || new Subscription()
      );

      // Подписка на изменения языка
      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
        })
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadUsers(): void {
    if (this.searchTerm) {
      this.searchUsers();
    } else {
      this.loadAllUsers();
    }
  }

  loadAllUsers(): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.userService.getUsers().subscribe({
        next: (users) => {
          this.users = users;
          this.filteredUsers = users;
          this.totalElements = users.length;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('UsersList: Error loading users', err);
          if (err.status === 403) {
            this.error = 'Ошибка доступа (403). Проверьте, что вы авторизованы как администратор.';
          } else if (err.status === 401) {
            this.error = 'Сессия истекла. Пожалуйста, войдите снова.';
            this.router.navigate(['/login']);
          } else {
            this.error = 'Ошибка при загрузке пользователей';
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  searchUsers(): void {
    this.loading = true;
    this.error = null;

    const params: SearchUsersParams = {
      searchTerm: this.searchTerm || undefined,
      page: this.currentPage,
      size: this.pageSize
    };

    this.subscriptions.add(
      this.userService.searchUsers(params).subscribe({
        next: (response: PageResponse<UserDto>) => {
          this.filteredUsers = response.content;
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.currentPage = response.number;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('UsersList: Error searching users', err);
          if (err.status === 403) {
            this.error = 'Ошибка доступа (403). Проверьте, что вы авторизованы как администратор.';
          } else if (err.status === 401) {
            this.error = 'Сессия истекла. Пожалуйста, войдите снова.';
            this.router.navigate(['/login']);
          } else {
            this.error = 'Ошибка при поиске пользователей';
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  viewUser(id: number): void {
    this.router.navigate(['/admin/users', id]);
  }

  // ── «Войти как» (временная функция поддержки) ──────────────────────────────

  get loginAsText(): string {
    return this.translated('admin.users.loginAs', 'Войти как');
  }

  /** Под администратором и под самим собой войти нельзя (бэкенд тоже запрещает). */
  canLoginAs(user: UserDto): boolean {
    return user.role?.code !== 'ROLE_ADMIN' && user.id !== this.authService.getUser()?.id;
  }

  async loginAs(user: UserDto): Promise<void> {
    const name = user.fullName || user.email;
    const confirmed = await this.confirmDialog.confirm(
      this.translated('admin.users.loginAsConfirm', 'Войти под пользователем {name}? Все ваши действия будут записаны в журнал.')
        .replace('{name}', name),
      { confirmText: this.loginAsText, danger: false }
    );
    if (!confirmed) {
      return;
    }
    this.subscriptions.add(
      this.authService.startImpersonation(user.id).subscribe({
        next: response => this.redirectByRole(response.user?.role?.code),
        error: err => {
          const message = err?.error?.message || this.translated('admin.users.loginAsError', 'Не удалось войти под пользователем');
          void this.confirmDialog.alert(message);
        }
      })
    );
  }

  private redirectByRole(roleCode?: string): void {
    switch (roleCode) {
      case 'ROLE_CARGO_OWNER':
        this.router.navigate(['/cargo-owner/dashboard']);
        break;
      case 'ROLE_FREIGHT_FORWARDER':
        this.router.navigate(['/freight-forwarder/dashboard']);
        break;
      case 'ROLE_PUBLIC':
        this.router.navigate(['/public/dashboard']);
        break;
      case 'ROLE_SUPPLIER':
        this.router.navigate(['/supplier/dashboard']);
        break;
      default:
        this.router.navigate(['/']);
        break;
    }
  }

  createUser(): void {
    this.router.navigate(['/admin/users/create']);
  }

  clearSearch(): void {
    this.searchForm.patchValue({ searchTerm: '' });
    this.searchTerm = '';
    this.currentPage = 0;
    this.loadAllUsers();
  }

  get visibleTabs(): RoleTab[] {
    return ROLE_TABS.filter(tab => tab.key === 'all' || this.roleCount(tab.key) > 0);
  }

  roleCount(key: string): number {
    return this.filteredUsers.filter(u => inTab(u, key)).length;
  }

  tabLabel(tab: RoleTab): string {
    return this.translated(tab.labelKey, tab.fallback);
  }

  get allUsersText(): string {
    return this.translated('admin.users.tabs.allUsers', 'Все пользователи');
  }

  /** Подпись кнопки выбора роли на телефоне. */
  get activeTabMobileLabel(): string {
    if (this.activeRole === 'all') return this.allUsersText;
    const tab = ROLE_TABS.find(t => t.key === this.activeRole);
    return tab ? this.tabLabel(tab) : this.allUsersText;
  }

  selectRole(key: string): void {
    this.activeRole = key;
  }

  get sortedUsers(): UserDto[] {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const value = (u: UserDto): string | number => {
      switch (this.sortKey) {
        case 'createdAt': return u.createdAt ? new Date(u.createdAt).getTime() : 0;
        default: return (u[this.sortKey] || '').toString().toLowerCase();
      }
    };
    const users = this.filteredUsers.filter(u => inTab(u, this.activeRole));
    return [...users].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'ru') * dir;
    });
  }

  /** Повторный клик по тому же столбцу меняет направление. */
  sortBy(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = key === 'createdAt' ? 'desc' : 'asc';
    }
  }

  sortIcon(key: SortKey): string {
    if (this.sortKey !== key) return '↕';
    return this.sortDir === 'asc' ? '↑' : '↓';
  }

  groupAriaSort(group: { key: SortKey }[]): string | null {
    const col = group.find(c => c.key === this.sortKey);
    return col ? this.ariaSort(col.key) : null;
  }

  ariaSort(key: SortKey): string | null {
    if (this.sortKey !== key) return null;
    return this.sortDir === 'asc' ? 'ascending' : 'descending';
  }

  initials(name?: string): string {
    return (name || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('');
  }

  getRoleName(role: any): string {
    return role?.name || 'Не указана';
  }

  private updateTranslations(): void {
    const titleTranslated = this.languageService.translate('admin.users.title');
    this.titleText = titleTranslated && titleTranslated !== 'admin.users.title' ? titleTranslated : 'Управление пользователями';
    
    const searchPlaceholderTranslated = this.languageService.translate('admin.users.searchPlaceholder');
    this.searchPlaceholderText = searchPlaceholderTranslated && searchPlaceholderTranslated !== 'admin.users.searchPlaceholder' ? searchPlaceholderTranslated : 'Поиск по имени, email или телефону...';
    
    const createUserTranslated = this.languageService.translate('admin.users.createUser');
    this.createUserText = createUserTranslated && createUserTranslated !== 'admin.users.createUser' ? createUserTranslated : 'Создать пользователя';
    
    const nameTranslated = this.languageService.translate('admin.users.name');
    this.nameText = nameTranslated && nameTranslated !== 'admin.users.name' ? nameTranslated : 'Имя';
    
    const emailTranslated = this.languageService.translate('admin.users.email');
    this.emailText = emailTranslated && emailTranslated !== 'admin.users.email' ? emailTranslated : 'Email';
    
    const phoneTranslated = this.languageService.translate('admin.users.phone');
    this.phoneText = phoneTranslated && phoneTranslated !== 'admin.users.phone' ? phoneTranslated : 'Телефон';
    
    const roleTranslated = this.languageService.translate('admin.users.role');
    this.roleText = roleTranslated && roleTranslated !== 'admin.users.role' ? roleTranslated : 'Роль';
    
    const actionsTranslated = this.languageService.translate('admin.users.actions');
    this.actionsText = actionsTranslated && actionsTranslated !== 'admin.users.actions' ? actionsTranslated : 'Действия';
    
    const viewTranslated = this.languageService.translate('admin.users.view');
    this.viewText = viewTranslated && viewTranslated !== 'admin.users.view' ? viewTranslated : 'Просмотр';
    
    const editTranslated = this.languageService.translate('admin.users.edit');
    this.editText = editTranslated && editTranslated !== 'admin.users.edit' ? editTranslated : 'Редактировать';
    
    const deleteTranslated = this.languageService.translate('admin.users.delete');
    this.deleteText = deleteTranslated && deleteTranslated !== 'admin.users.delete' ? deleteTranslated : 'Удалить';
    
    const noUsersTranslated = this.languageService.translate('admin.users.noUsers');
    this.noUsersText = noUsersTranslated && noUsersTranslated !== 'admin.users.noUsers' ? noUsersTranslated : 'Пользователи не найдены';
    
    const loadingTranslated = this.languageService.translate('admin.users.loading');
    this.loadingText = loadingTranslated && loadingTranslated !== 'admin.users.loading' ? loadingTranslated : 'Загрузка...';
    
    this.registeredText = this.translated('admin.users.registeredAt', 'Регистрация');

    this.cdr.markForCheck();
  }

  private translated(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }
}
